import axios from "axios";
import { toast } from "sonner";
import { getSessionCache, setSessionCache, clearSessionCache as clearClientSessionCache } from "../utils/cache";
export { clearClientSessionCache as clearSessionCache };
import type { LoginFormData, LoginResponse } from "../types/login.types";
import type { RegistrationFormData, RegisterResponse } from "../types/registration.types";
import type { DealerProfile, ShopifyAssociateResponse } from "../types/dealer.types";
import type { TiersResponse } from "../types/tier.types";
import type { LeadsResponse } from "../types/leads.types";
import type {
    BulkActionPayload,
    BulkActionResponse,
    AdminDealerListResponse,
    AdminDashboardStats,
    AdminOnboardingStatsResponse
} from "../types/admin.types";
import type { DealerPage } from "../pages/admin/page-management/AdminPageManagement";
import type { IData } from "../pages/ClaimPage";
import type { ClaimRequest } from "../pages/admin/page-management/ClaimRequest";
import type { RemovalRequest } from "../pages/admin/page-management/RemovalRequest";
import type { SurveyForm, SurveySubmissionsQueryParams, SurveySubmissionsResponse, CreateSurveyPayload, UpdateSurveyPayload, SubmitDealerSurveyPayload, AiFeedbackAnalysis } from "../types/survey.types";
import type { VersionHistoryResponse, CreateReleaseNotePayload, CreateReleaseNoteResponse } from "../types/versionHistory.types";


const base_url = import.meta.env.VITE_API_URL || "https://locator.framemytv.com/api";

export const api = axios.create({
    baseURL: base_url,
    headers: {
        "Content-Type": "application/json",
    },
});

export const apiFile = axios.create({
    baseURL: base_url,
    headers: {
        "Content-Type": "multipart/form-data",
    },
});

// Attach token dynamically
const attachToken = (config: any) => {
    const isAdminRoute = config.url?.includes("/admin");
    const isCmRoute = config.url?.includes("/content-manager");
    const adminToken = localStorage.getItem("admin_token");
    const dealerToken = localStorage.getItem("dealer_token");
    const cmToken = localStorage.getItem("cm_token");

    if (isAdminRoute && adminToken) {
        config.headers.Authorization = `Bearer ${adminToken}`;
    } else if (isCmRoute && cmToken) {
        config.headers.Authorization = `Bearer ${cmToken}`;
    } else if (dealerToken) {
        config.headers.Authorization = `Bearer ${dealerToken}`;
    } else if (adminToken) {
        config.headers.Authorization = `Bearer ${adminToken}`;
    } else if (cmToken) {
        config.headers.Authorization = `Bearer ${cmToken}`;
    }

    return config;
};

api.interceptors.request.use(attachToken);
apiFile.interceptors.request.use(attachToken);

// Response Interceptor to catch 401 and 500
const handleApiError = (error: any) => {
    // 1. Handle 401 Unauthorized
    if (error.response && error.response.status === 401) {
        // Prevent redirect validity check for login endpoint itself
        const isLoginRequest = error.config?.url?.includes("/login");

        if (!isLoginRequest && !window.location.pathname.includes("/login")) {
            const isAdminEndpoint = error.config?.url?.includes("/admin");
            const impersonationType = localStorage.getItem("type");
            const isImpersonating = impersonationType === "admin" || impersonationType === "url_impersonation";
            console.warn("401 detected → clearing session & redirecting");

            if (isAdminEndpoint && !isImpersonating) {
                // Admin token failed (and not just an impersonating admin calling a dealer-admin endpoint)
                localStorage.removeItem("admin_token");
                localStorage.removeItem("admin_role");
                localStorage.removeItem("admin_user");
                window.location.href = "/admin/login";
            } else {
                // Dealer token failed (or impersonation token failed)
                localStorage.removeItem("dealer_token");
                localStorage.removeItem("dealer_role");
                localStorage.removeItem("dealer_user");

                if (isImpersonating) {
                    // If it was an impersonation session, clear the type
                    localStorage.removeItem("type");

                    if (impersonationType === "url_impersonation") {
                        // URL-based impersonation (like from locator backend) should redirect to normal login
                        window.location.href = "/login";
                    } else {
                        // Standard admin impersonation goes back to admin dealers
                        window.location.href = "/admin/dealers";
                    }
                } else {
                    // Normal dealer session expired
                    window.location.href = "/login";
                }
            }
        }
    }
    // 2. Handle 500 and Network Errors Globally
    else if (!error.response) {
        // No response means the server is unreachable (Network Error, CORS, Offline)
        toast.error("Network Error: Cannot connect to the server. Please check your internet connection.", { id: "global-network-error" });
    } else if (error.response.status >= 500) {
        // 5xx means the server crashed
        toast.error("Internal Server Error: The server encountered a problem. Please try again later.", { id: "global-server-error" });
    }

    return Promise.reject(error);
};

api.interceptors.response.use((response) => response, handleApiError);
apiFile.interceptors.response.use((response) => response, handleApiError);

// ========================
// AUTH API ENDPOINTS
// ========================
export const login = (data: LoginFormData) =>
    api.post<LoginResponse>("/dealer/login", data);

export const adminLogin = (data: LoginFormData) =>
    api.post<LoginResponse>("/dealer/admin/login", data);

export const contentManagerLogin = (data: LoginFormData) =>
    api.post<LoginResponse>("/content-manager/login", data);

export const register = (data: RegistrationFormData) =>
    api.post<RegisterResponse>("/dealer/register", data);
export const forgotPassword = (data: { email: string }) =>
    api.post<{ success: boolean; message: string }>("/dealer/forgot-password", data);

// ========================
// GOOGLE BUSINESS PROFILE API ENDPOINTS
// ========================
export const openGmbConnect = async () => {
    const dealerToken = localStorage.getItem("dealer_token");
    const adminToken = localStorage.getItem("admin_token");

    if (!dealerToken && !adminToken) {
        throw new Error("Please sign in before connecting Google Business Profile.");
    }

    const response = await api.get<{
        success?: boolean;
        message?: string;
        url?: string;
        data?: { url?: string };
    }>("/gmb/connect");

    const url = response.data?.url ?? response.data?.data?.url;

    if (!url) {
        throw new Error(response.data?.message || "Google Business Profile connect URL was not returned.");
    }

    return url;
};

export const getGmbStatus = () =>
    api.get<{ success: boolean; data: any; message?: string }>("/gmb/status");

export const pullGmbData = () =>
    api.get<{ success: boolean; data: any; message?: string }>("/gmb/pull");
export const pullGmbDataImage = () =>
    api.get<{ success: boolean; data: any; message?: string }>("/gmb/media");
export const pullGmbServices = () =>
    api.post<{ success: boolean; data: any; message?: string }>("/gmb/services/sync");

export const pullGmbReviews = () =>
    api.get<{ success: boolean; data: any; message?: string }>("/gmb/reviews");
export const submitGmbCallback = (data: { code?: string; state?: string }) =>
    api.post<{ success: boolean; data?: any; message?: string }>("/gmb/callback", data);

// ========================
// DEALER API ENDPOINTS
// ========================
export const getDealerProfile = () =>
    api.get<{ success: boolean; data: DealerProfile }>("/dealer/profile");

export const updateDealerProfile = (data: Partial<DealerProfile>) =>
    api.put<{ success: boolean; data: DealerProfile; message: string }>("/dealer/profile", data);

export const requestReverification = (note: string) =>
    api.post<{ success: boolean; message: string; data: DealerProfile }>("/dealer/request-reverification", { note });


export const toggleDealerPublished = (data: { isPublished: boolean }) =>
    api.put<{ success: boolean; message: string; data: DealerProfile }>("/dealer/toggle-published", data);

export const updateVisibilitySettings = (data: { showGallery?: boolean; showReviews?: boolean; showServices?: boolean; showAbout?: boolean }) =>
    api.put<{ success: boolean; message: string; data: DealerProfile }>("/dealer/visibility-settings", data);

export const refreshDealerRevenue = () =>
    api.post<{ success: boolean; message: string; data?: any }>("/dealer/refresh-revenue");

export const getStoreCredit = () =>
    api.get<{ success: boolean; data: { balance: number; currency?: string; history?: any[] } }>("/dealer/shopify/store-credit");

export const getShopifyOrders = (limit = 20, cursor?: string, direction: 'after' | 'before' = 'after') => {
    let url = `/dealer/shopify/orders?limit=${limit}`;
    if (cursor) {
        const paramName = direction === 'after' ? 'cursor' : 'before';
        url += `&${paramName}=${encodeURIComponent(cursor)}`;
    }
    return api.get<{ success: boolean; data: any }>(url);
};

export const getShopifyOrderPaymentMethods = (limit = 20, cursor?: string, direction: 'after' | 'before' = 'after') => {
    let url = `/dealer/shopify/orders/payment-methods?limit=${limit}`;
    if (cursor) {
        const paramName = direction === 'after' ? 'cursor' : 'before';
        url += `&${paramName}=${encodeURIComponent(cursor)}`;
    }
    return api.get<{ success: boolean; data: any }>(url);
}; export const getPaymentGatewaySummary = () =>
    api.get<{ success: boolean; message: string; data?: any }>("/dealer/shopify/payment-gateway-summary");

export const getShopifyOrdersHistory = (year: number) =>
    api.get<{ success: boolean; message: string; data: { graph: any } }>(`/dealer/shopify/orders/history?year=${year}`);

export const getShopifyDemoProducts = (limit: number = 20, cursor?: string, direction: 'after' | 'before' = 'after') => {
    let url = `/dealer/shopify/products?productType=demo&status=active&limit=${limit}`;
    if (cursor) {
        const paramName = direction === 'after' ? 'cursor' : 'before';
        url += `&${paramName}=${encodeURIComponent(cursor)}`;
    }
    return api.get<{ success: boolean; message: string; data: { shop: string; products: any[]; pageInfo: any } }>(url);
};

// ========================
// SHOWROOM API ENDPOINTS
// ========================
export const getShowroomItems = () =>
    api.get<{ success: boolean; message: string; data: { defaultItems: any[]; demoFrames: any[] } }>('/dealer/shopify/showroom');

export interface RequestDemoFramePayload {
    productId: string;
    message: string;
    showroomType: string;
    showroomTypeOther?: string;
    installationAddress: string;
    screenSize?: string;
    modelYear?: string;
    tradeShowEventNameDates?: string;
    willGoToShowroomAfterEvent?: boolean;
    image?: File | null;
}

export const requestDemoFrame = (payload: RequestDemoFramePayload | FormData | any) => {
    if (payload instanceof FormData) {
        return apiFile.post<{ success: boolean; message: string; data?: any }>('/dealer/shopify/frame-collection/request-demo', payload);
    }
    const formData = new FormData();
    formData.append('productId', payload.productId);
    formData.append('message', payload.message);
    formData.append('showroomType', payload.showroomType);
    if (payload.showroomTypeOther) {
        formData.append('showroomTypeOther', payload.showroomTypeOther);
    }
    formData.append('installationAddress', payload.installationAddress);
    
    if (payload.screenSize) {
        formData.append('screenSize', payload.screenSize);
    }
    if (payload.modelYear) {
        formData.append('modelYear', payload.modelYear);
    }
    if (payload.tradeShowEventNameDates) {
        formData.append('tradeShowEventNameDates', payload.tradeShowEventNameDates);
    }
    if (payload.willGoToShowroomAfterEvent !== undefined) {
        formData.append('willGoToShowroomAfterEvent', String(payload.willGoToShowroomAfterEvent));
    }
    
    if (payload.image) {
        formData.append('image', payload.image);
    }
    return apiFile.post<{ success: boolean; message: string; data?: any }>('/dealer/shopify/frame-collection/request-demo', formData);
};

export const removeShowroomItem = (itemId: number | string) =>
    api.delete<{ success: boolean; message: string }>(`/dealer/shopify/showroom/items/${itemId}`);

export const getShopifyFrameCollection = (productType: string = 'all', limit: number = 12, cursor?: string, direction: 'after' | 'before' = 'after') => {
    let url = `/dealer/shopify/frame-collection?productType=${encodeURIComponent(productType)}&limit=${limit}`;
    if (cursor) {
        url += `&${direction === 'before' ? 'before' : 'after'}=${encodeURIComponent(cursor)}`;
    }
    return api.get<any>(url);
};

// ========================
// TIER API ENDPOINTS
// ========================
export const getTiers = async () => {
    const cached = getSessionCache<TiersResponse>("tiers_catalog");
    if (cached) return { data: cached } as any;
    const res = await api.get<TiersResponse>("/tiers");
    if (res.data) setSessionCache("tiers_catalog", res.data, 10);
    return res;
};




// ========================
// ADMIN API ENDPOINTS
// ========================

export const getAdminDealers = (page = 1, limit = 20, search?: string, tier?: string) => {
    let url = `/admin/dealers?page=${page}&limit=${limit}`;
    if (search && search.trim() !== "") {
        url += `&search=${encodeURIComponent(search)}`;
    }
    if (tier && tier !== "All" && tier !== "all") {
        url += `&type=${encodeURIComponent(tier)}`;
    }
    return api.get<AdminDealerListResponse>(url);
};

export const getAdminOnboardedDealers = (page = 1, limit = 20, search?: string, isOnboarded?: boolean, tier?: string) => {
    let url = `/admin/dealers/onboarded?page=${page}&limit=${limit}`;
    if (search && search.trim() !== "") {
        url += `&search=${encodeURIComponent(search)}`;
    }
    if (isOnboarded !== undefined) {
        url += `&isOnboarded=${isOnboarded}`;
    }
    if (tier && tier !== "all" && tier !== "All") {
        url += `&tier=${encodeURIComponent(tier)}`;
    }
    return api.get<AdminDealerListResponse>(url);
};

export const getAdminDealerById = (id: number | string) =>
    api.get<{ success: boolean; data: any }>(`/admin/dealers/${id}`);

export const getAdminDealerStoreCredit = (id: number | string) =>
    api.get<{ success: boolean; data: any }>(`/admin/dealers/${id}/shopify/store-credit`);

export const getAdminDealerShopifyOrders = (id: number | string, limit = 20, cursor?: string, direction: 'after' | 'before' = 'after') => {
    let url = `/admin/dealers/${id}/shopify/orders?limit=${limit}`;
    if (cursor) {
        const paramName = direction === 'after' ? 'cursor' : 'before';
        url += `&${paramName}=${encodeURIComponent(cursor)}`;
    }
    return api.get<{ success: boolean; data: any }>(url);
};

export const getAdminDealerShopifyOrderPaymentMethods = (id: number | string, limit = 20, cursor?: string, direction: 'after' | 'before' = 'after') => {
    let url = `/admin/dealers/${id}/shopify/orders/payment-methods?limit=${limit}`;
    if (cursor) {
        const paramName = direction === 'after' ? 'cursor' : 'before';
        url += `&${paramName}=${encodeURIComponent(cursor)}`;
    }
    return api.get<{ success: boolean; data: any }>(url);
};

export const getAdminDashboardStats = async (hideMajorPartner: boolean = false, hideMinorPartner: boolean = false) => {
    const cacheKey = `admin_dashboard_stats_${hideMajorPartner}_${hideMinorPartner}`;
    const cached = getSessionCache<AdminDashboardStats>(cacheKey);
    if (cached) return { data: { success: true, data: cached } } as any;

    let url = "/admin/analytics/dashboard";
    const params = new URLSearchParams();
    params.append("majorPartner", hideMajorPartner.toString());
    params.append("minorPartner", hideMinorPartner.toString());
    const qs = params.toString();
    if (qs) url += `?${qs}`;
    const res = await api.get<{ success: boolean; data: AdminDashboardStats }>(url);
    if (res.data?.success && res.data.data) {
        setSessionCache(cacheKey, res.data.data, 5);
    }
    return res;
};

export const getAdminOnboardingStats = () =>
    api.get<AdminOnboardingStatsResponse>("/admin/analytics/onboarding");

// Refresh Revenue APIs
export const startRefreshRevenueAll = () =>
    api.post<{ success: boolean; data: any; jobId?: string; message: string }>("/dealer/admin/refresh-revenue-all");

export const getRefreshRevenueStatus = () =>
    api.get<{ success: boolean; data: { total: number; processed: number; updated: number; skippedNotFound: number; failed: number; status?: string } }>("/dealer/admin/refresh-revenue-all");

// Shopify page management APIs
export const getShopifyPages = () =>
    api.get<{ success: boolean; data: { pages: DealerPage[], claimRequests: number, removalRequests: number }; message?: string }>("/admin/pages");

export const getContentManagerPages = (page: number = 1, limit: number = 20) =>
    api.get<{ success: boolean; data: DealerPage[]; pagination: { page: number; limit: number; total: number; totalPages: number }; message?: string }>(`/content-manager/pages?page=${page}&limit=${limit}`);

export const getContentManagerPageDetails = (pageId: number | string) =>
    api.get<{ success: boolean; data: any; message: string }>(`/content-manager/pages/${pageId}`);

export const publishPage = (pageId: number) =>
    api.put<{ success: boolean; message: string }>(`/admin/page/${pageId}/publish`);

export const unPublishPage = (pageId: number) =>
    api.put<{ success: boolean; message: string }>(`/admin/page/${pageId}/unpublish`);

export const contentManagerPublishPage = (pageId: number) =>
    api.put<{ success: boolean; message: string }>(`/content-manager/page/${pageId}/publish`);

export const contentManagerUnPublishPage = (pageId: number) =>
    api.put<{ success: boolean; message: string }>(`/content-manager/page/${pageId}/unpublish`);

export const getClaimedRequests = () =>
    api.get<{ success: boolean, data: ClaimRequest[], message: string }>("/admin/claim-requests")

export const getRemovalRequests = () =>
    api.get<{ success: boolean, data: RemovalRequest[], message: string }>("/admin/removal-requests")

export const approveClaimRequest = (claimId: number) =>
    api.put<{ success: boolean, message: string }>(`/admin/claim-requests/${claimId}/approve`)

export const rejectClaimRequest = (claimId: number, note: string) =>
    api.put<{ success: boolean, message: string }>(`/admin/claim-requests/${claimId}/reject`, { note })

export const approveRemovalRequest = (rejectId: number) =>
    api.put<{ success: boolean, message: string }>(`/admin/removal-requests/${rejectId}/approve`)

export const rejectRemoveRequest = (rejectId: number, reason: string) =>
    api.put<{ success: boolean, message: string }>(`/admin/removal-requests/${rejectId}/reject`, { reason })

export const getPageEligibility = () =>
    api.get<{ success: boolean; message: string, data: IData }>("/dealer/eligibility")

export const claimRequest = () =>
    api.post<{ success: boolean; message: string }>("/dealer/page/claim-request")

export const removalRequest = () =>
    api.post<{ success: boolean; message: string }>("/dealer/page/removal-request")

export const createShopifyPage = () =>
    api.post<{ success: Boolean; message: string }>("/dealer/page")

export const updateShopifyPage = (metafields: Record<string, string>) =>
    api.put<{ success: Boolean; message: string }>("/dealer/page", { metafields })

export const updateDealerPageContent = (content: string, reason?: string) =>
    api.put<{ success: boolean; data: any; message: string }>("/dealer/page/content", { content, reason });

export const getDealerContentEditRequest = () =>
    api.get<{ success: boolean; data: any; message: string }>("/dealer/page/content-request");

export const getDealerPageDetails = () =>
    api.get<{ success: boolean; data: any; message: string }>("/dealer/page")

export const getDealerListingImage = () =>
    api.get<{ success: boolean; data: { companyImage: string | null }; message: string }>("/dealer/page/listing-image")

export const uploadDealerListingImage = (file: File) => {
    const formData = new FormData();
    formData.append("image", file);
    return apiFile.post<{ success: boolean; data: any; message: string }>("/dealer/page/listing-image", formData);
};

export const deleteDealerListingImage = () =>
    api.delete<{ success: boolean; message: string }>("/dealer/page/listing-image")

// Admin Shopify Page Edit APIs
export const getAdminDealerPageDetails = (pageId: number | string) =>
    api.get<{ success: boolean; data: any; message: string }>(`/admin/page/${pageId}/details`)

export const getAdminPageAssets = (noCache?: boolean) =>
    api.get<{ success: boolean; message: string; data: { files: Array<{ gid: string; url: string; alt: string; width: number; height: number; mimeType: string; createdAt: string }>; pageInfo: { hasNextPage: boolean; endCursor: string } } }>(`/admin/page/assets${noCache ? `?_=${Date.now()}` : ""}`)

export const uploadAdminPageAsset = (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return apiFile.post<{ success: boolean; message: string; data: { gid: string; url: string; alt: string; width: number; height: number; mimeType: string; createdAt: string } }>("/admin/page/assets/upload", formData);
};

export const updateAdminShopifyPage = (pageId: number | string, title: string, content: string, metafields: Record<string, string>) =>
    api.put<{ success: boolean; message: string }>(`/admin/page/${pageId}`, { title, content, metafields })

export const updateContentManagerShopifyPage = (pageId: number | string, title: string, content: string) =>
    api.put<{ success: boolean; message: string }>(`/content-manager/pages/${pageId}`, { title, content })

export const getPageHistory = (pageId: number | string) =>
    api.get<{ success: boolean; message: string; data: PageHistoryEntry[] }>(`/admin/page/${pageId}/history`)

export interface PageHistoryEntry {
    id: number;
    dealerId?: number;
    dealerShopifyPageId?: number;
    fieldName: string;
    oldValue: string | null;
    newValue: string | null;
    action: string;
    changedBy: string;
    createdAt: string;
}

export interface PageContentLogEntry {
    id: number;
    dealerId: number;
    dealerPageId: number;
    oldContent: string | null;
    newContent: string | null;
    action: string;
    changedBy: string;
    notes: string | null;
    createdAt: string;
    dealer?: {
        id: number;
        companyName: string;
        email: string;
    };
}

export interface ContentEditRequestEntry {
    id: number;
    dealerId: number;
    dealerPageId: number;
    requestedContent: string;
    currentContent: string | null;
    reason: string | null;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    rejectionReason: string | null;
    reviewedBy: number | null;
    reviewerRole: string | null;
    reviewedAt: string | null;
    createdAt: string;
    updatedAt: string;
    dealer?: {
        id: number;
        companyName: string;
        email: string;
        phone: string;
        city: string;
        state: string;
    };
    dealerPage?: {
        id: number;
        shopifyPageId: string;
        shopifyPageHandle: string;
        shopifyPageUrl: string;
        status: string;
        contentEditCount: number;
        maxContentEdits: number;
    };
}

// Page Body Content Logs & Request Management
export const getPageContentHistory = (pageId: number | string) =>
    api.get<{ success: boolean; message: string; data: PageContentLogEntry[] }>(`/admin/page/${pageId}/content-history`);

export const adjustDealerContentEditLimit = (
    pageId: number | string,
    data: { contentEditCount?: number; maxContentEdits?: number; resetToZero?: boolean }
) =>
    api.put<{ success: boolean; data: any; message: string }>(`/admin/page/${pageId}/content-limit`, data);

export const getAdminContentEditRequests = (page = 1, limit = 20, status?: string, search?: string) => {
    let url = `/admin/page/content-requests?page=${page}&limit=${limit}`;
    if (status && status !== "ALL") url += `&status=${status}`;
    if (search && search.trim() !== "") url += `&search=${encodeURIComponent(search)}`;
    return api.get<{ success: boolean; message: string; data: { requests: ContentEditRequestEntry[]; pagination: any } }>(url);
};

export const approveAdminContentEditRequest = (requestId: number) =>
    api.put<{ success: boolean; message: string }>(`/admin/page/content-requests/${requestId}/approve`);

export const rejectAdminContentEditRequest = (requestId: number, rejectionReason?: string) =>
    api.put<{ success: boolean; message: string }>(`/admin/page/content-requests/${requestId}/reject`, { rejectionReason });

// Content Manager Page Content APIs
export const getContentManagerContentEditRequests = (page = 1, limit = 20, status?: string, search?: string) => {
    let url = `/content-manager/page/content-requests?page=${page}&limit=${limit}`;
    if (status && status !== "ALL") url += `&status=${status}`;
    if (search && search.trim() !== "") url += `&search=${encodeURIComponent(search)}`;
    return api.get<{ success: boolean; message: string; data: { requests: ContentEditRequestEntry[]; pagination: any } }>(url);
};

export const approveContentManagerContentEditRequest = (requestId: number) =>
    api.put<{ success: boolean; message: string }>(`/content-manager/page/content-requests/${requestId}/approve`);

export const rejectContentManagerContentEditRequest = (requestId: number, rejectionReason?: string) =>
    api.put<{ success: boolean; message: string }>(`/content-manager/page/content-requests/${requestId}/reject`, { rejectionReason });

export const getContentManagerPageContentHistory = (pageId: number | string) =>
    api.get<{ success: boolean; message: string; data: PageContentLogEntry[] }>(`/content-manager/pages/${pageId}/content-history`);

// Profile Update Requests APIs
export const getAdminUpdateRequests = (page = 1, limit = 20, status?: string, search?: string) => {
    let url = `/admin/profile/update-requests?page=${page}&limit=${limit}`;
    if (status && status !== "ALL") url += `&status=${status}`;
    if (search && search.trim() !== "") url += `&search=${encodeURIComponent(search)}`;
    return api.get<{ success: boolean; message: string; data: { requests: any[]; pagination: any } }>(url);
};

export const approveUpdateRequest = (requestId: number) =>
    api.put<{ success: boolean; message: string }>(`/admin/profile/update-requests/${requestId}/approve`);

export const rejectUpdateRequest = (requestId: number, rejectionReason: string) =>
    api.put<{ success: boolean; message: string }>(`/admin/profile/update-requests/${requestId}/reject`, { rejectionReason });

// Admin Profile & Security APIs
export const getAdminProfile = () => {
    return api.get<{ success: boolean; data: any }>("/admin/profile");
};

export const updateAdminProfile = (data: any) => {
    return api.put<{ success: boolean; data: any; message: string }>("/admin/profile", data);
};

export const changeAdminPassword = (data: any) => {
    return api.put<{ success: boolean; message: string }>("/admin/change-password", data);
};

export interface DealerFeedbackPayload {
    stars: number;
    type: "DEALER";
    usabilityRating?: string;
    navigationEase?: string;
    comment: string;
    favoriteThing?: string;
    improvementSuggestions?: string;
}

export const submitDealerFeedback = async (payload: DealerFeedbackPayload) => {
    try {
        const res = await api.post<{ success: boolean; message: string; data?: any }>("/dealer/feedback", payload);
        return res;
    } catch (err: any) {
        // If 404, 501, or network error while endpoint is not yet live on backend, provide mock fallback
        if (!err.response || err.response.status === 404 || err.response.status === 501) {
            console.warn("Backend /dealer/feedback endpoint not active yet. Simulating local success.", payload);
            await new Promise((resolve) => setTimeout(resolve, 600));
            return {
                data: {
                    success: true,
                    message: "Thank you! Your feedback has been recorded.",
                    data: payload,
                },
            } as any;
        }
        throw err;
    }
};

export const getAdminFeedbacks = async (type?: "LOCATOR" | "DEALER") => {
    const cacheKey = `admin_feedbacks_${type || "all"}`;
    const cached = getSessionCache<any>(cacheKey);
    if (cached && cached.stats) return { data: cached } as any;
    let url = "/admin/feedbacks";
    if (type) url += `?type=${type}`;
    const res = await api.get<{ success: boolean; data: any[]; stats?: any }>(url);
    if (res.data) setSessionCache(cacheKey, res.data, 5);
    return res;
};

export const getAdminLocationAnalytics = async () => {
    const cached = getSessionCache<any>("admin_location_analytics");
    if (cached) return { data: cached } as any;
    const res = await api.get<{ success: boolean; data: any }>("/admin/analytics/locations");
    if (res.data) setSessionCache("admin_location_analytics", res.data, 5);
    return res;
};

export const impersonateDealer = (dealerId: number) =>
    api.post<{ success: boolean; message: string; data: { token: string; dealerId: number; type: string } }>(`/admin/impersonate`, { dealerId });

export const approveDealer = (dealerId: number) =>
    api.put<{ success: boolean; message: string }>(`/admin/dealers/${dealerId}/approve`);

export const rejectDealer = (dealerId: number, reasonCode: string, customNote?: string) =>
    api.put<{ success: boolean; message: string }>(`/admin/dealers/${dealerId}/reject`, { reasonCode, customNote });

export const bulkAdminDealerAction = (payload: BulkActionPayload) =>
    api.post<BulkActionResponse>("/admin/dealers/bulk-action", payload);

export const deleteAdminDealer = (id: number | string, permanent = true) =>
    api.delete<{ success: boolean; message: string }>(`/admin/dealers/${id}?permanent=${permanent}`);

// ========================
// BACKLINK API ENDPOINTS
// ========================
export const verifyBacklink = (url: string) =>
    api.post<{ success: boolean; message: string; data?: any }>("/dealer/backlink/verify", { url });

// Admin Backlink Endpoints
export const getAdminBacklinks = () =>
    api.get<{ success: boolean; data: any[] }>("/admin/backlinks"); // Adjust return type as needed

export const updateAdminBacklink = (id: number, url: string) =>
    api.put<{ success: boolean; message: string }>(`/admin/backlinks/${id}`, { url });

// ========================
// SYSTEM ADMIN ENDPOINTS
// ========================

export const clearSystemCache = () => {
    clearClientSessionCache();
    return new Promise<{ success: boolean; message: string }>((resolve) => {
        setTimeout(() => {
            resolve({ success: true, message: "System cache cleared successfully" });
        }, 1500);
    });
};

export const runSystemBackup = () => {
    return new Promise<{ success: boolean; message: string }>((resolve) => {
        setTimeout(() => {
            resolve({ success: true, message: "Manual backup completed successfully" });
        }, 3000); // Takes a bit longer
    });
};

// ========================
// DEALER DASHBOARD ENDPOINTS
// ========================
export const getDealerStats = () =>
    api.get<{ success: boolean; data: any }>("/dealer/stats");

export const getRecentLeads = (limit = 5) =>
    api.get<{ success: boolean; data: any[] }>(`/dealer/leads/recent?limit=${limit}`);

export const getRecentReviews = (limit = 5) =>
    api.get<{ success: boolean; data: any[] }>(`/dealer/reviews/recent?limit=${limit}`);

export const getDealerLeads = (page = 1, limit = 10, status?: string) => {
    let url = `/dealer/leads?page=${page}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    return api.get<LeadsResponse>(url);
};

export const getDealerLeadById = (id: number | string) =>
    api.get<{ success: boolean; data: any }>(`/dealer/leads/${id}`);

export const updateDealerLeadStatus = (id: number | string, status: string) =>
    api.put<{ success: boolean; data: any }>(`/dealer/leads/${id}`, { status });

export const updateDealerLeadNotes = (id: number | string, notes: string) =>
    api.put<{ success: boolean; data: any }>(`/dealer/leads/${id}`, { notes });

export const updateDealerLeadsBulk = (leadIds: (number | string)[], status: string) =>
    api.post<{ success: boolean; data: { leadIds: (number | string)[]; status: string } }>("/dealer/leads/bulk-update", { leadIds, status });

// ========================
// PUBLIC API ENDPOINTS
// ========================
export const getStoreLocatorDealers = (lat: number, lng: number, radius: number = 50, query?: string, page = 1, limit = 20, isFirstLoading?: boolean, email?: string) => {
    let url = `/public/dealers`;

    // If query is present, we only pass query and pagination (no location details)
    if (query && query.trim() !== '') {
        url += `?query=${encodeURIComponent(query)}&page=${page}&limit=${limit}`;
    } else {
        // Otherwise pass location details
        url += `?lat=${lat}&lng=${lng}&radius=${radius}&page=${page}&limit=${limit}`;
        if (isFirstLoading !== undefined) url += `&isFirstLoading=${isFirstLoading}`;
        if (email) url += `&email=${encodeURIComponent(email)}`;
    }

    return api.get<{ success: boolean; data: { dealers: any[], pagination: any } }>(url);
};

export const getPublicProfileByEmail = (email: string) =>
    api.post<{ success: boolean; data: any }>("/dealer/public-profile", { email });

export const getOnboardingStatus = (email: string) =>
    api.get<{ success: boolean; data: any }>(`/public/dealers/onboarding/status?email=${encodeURIComponent(email)}`);

// ========================
// CUSTOM SERVICES API ENDPOINTS
// ========================
export const getCustomServices = () =>
    api.get<{ success: boolean; data: any[] }>("/dealer/custom-services");

export const createCustomService = (data: { title: string; description?: string; price?: string }) =>
    api.post<{ success: boolean; data: any }>("/dealer/custom-services", data);

export const updateCustomService = (id: string | number, data: { title?: string; description?: string; price?: string; visible?: boolean }) =>
    api.put<{ success: boolean; data: any }>(`/dealer/custom-services/${id}`, data);

export const deleteCustomService = (id: string | number) =>
    api.delete<{ success: boolean; message: string }>(`/dealer/custom-services/${id}`);

export const reorderCustomServices = (serviceIds: (string | number)[]) =>
    api.put<{ success: boolean; message: string }>("/dealer/custom-services/reorder", { serviceIds });


export const submitLead = (dealerId: string | number, leadData: { name: string; email: string; phone: string; message: string; serviceType?: string }) => {
    return api.post<{ success: boolean; message: string }>(`/public/dealers/${dealerId}/leads`, leadData);
};

export const submitDealerReport = (reportData: {
    dealerId: string | number;
    reporterName: string;
    reporterEmail: string;
    reason: string;
    description: string
}) => {
    return api.post<{ success: boolean; message: string }>("/public/reports", reportData);
};


export const verifyEmail = (email: string) => {
    return api.post<{ success: boolean; message: string }>(`/dealer/backlink/verify`, { email });
};

// ========================
// DEALER TIER PROGRESS ENDPOINT
// ========================
export const getDealerTierProgress = async (months: number = 6, forceRefresh: boolean = false) => {
    const token = localStorage.getItem("dealer_token") || localStorage.getItem("admin_token") || "";
    const tokenKey = token ? token.slice(-16) : "guest";
    const cacheKey = `tier_progress_${tokenKey}_${months}`;
    if (!forceRefresh) {
        const cached = getSessionCache<any>(cacheKey);
        if (cached) return { data: cached } as any;
    }
    const res = await api.get<any>(`/dealer/tier-progress?months=${months}`);
    if (res.data) setSessionCache(cacheKey, res.data, 3);
    return res;
};

export interface RegionalStandings {
    cityRank: number;
    cityDealersCount: number;
    city: string;
    lastMonthCityRank?: number;
    cityRankChange?: number;
    last90DaysCityRank?: number;
    cityRankChange90Days?: number;
    territoryRank?: number;
    territoryDealersCount?: number;
    territory?: string;
    lastMonthTerritoryRank?: number;
    territoryRankChange?: number;
    last90DaysTerritoryRank?: number;
    territoryRankChange90Days?: number;
    stateRank: number;
    stateDealersCount: number;
    state: string;
    lastMonthStateRank?: number;
    stateRankChange?: number;
    last90DaysStateRank?: number;
    stateRankChange90Days?: number;
    nationalRank: number;
    nationalDealersCount: number;
    lastMonthNationalRank?: number;
    nationalRankChange?: number;
    last90DaysNationalRank?: number;
    nationalRankChange90Days?: number;
    performancePercentile: number;
    competitorsAheadInTier: number;
    totalDealersInTier: number;
    tierName: string;
    competitorsAheadOverall: number;
    leaderboard: {
        rank: number;
        companyName: string;
        revenue: number;
        tierName: string;
        isCurrentDealer: boolean;
        city: string;
        state: string;
    }[];
    scope: string;
    insights: {
        message: string;
        proTip: string;
    };
}

export const getRegionalStandings = async (scope: 'city' | 'territory' | 'state' | 'national' = 'state', forceRefresh: boolean = false) => {
    const token = localStorage.getItem("dealer_token") || localStorage.getItem("admin_token") || "";
    const tokenKey = token ? token.slice(-16) : "guest";
    const cacheKey = `regional_standings_${tokenKey}_${scope}`;
    if (!forceRefresh) {
        const cached = getSessionCache<any>(cacheKey);
        if (cached) return { data: cached } as any;
    }
    const res = await api.get<any>(`/dealer/regional-standings?scope=${scope}`);
    if (res.data) setSessionCache(cacheKey, res.data, 5);
    return res;
};


// ========================
// ANALYTICS ENDPOINTS
// ========================
export const sendAnalyticsEvents = (_events: any[]) => {
    // console.log(`[Mock Analytics] Sending batch of ${events.length} events to backend:`, events);
    return Promise.resolve({ data: { success: true } });
};

// ========================
// GALLERY API ENDPOINTS (Images & Videos)
// ========================

// Images
export const uploadDealerImage = (file: File, type?: string) => {
    const formData = new FormData();
    formData.append("image", file);
    if (type) formData.append("type", type);
    return apiFile.post<{ success: boolean; data: any; message: string }>("/dealer/images", formData);
};

export const getDealerImages = () =>
    api.get<{ success: boolean; data: any[] }>("/dealer/images");

export const deleteDealerImage = (id: number | string) =>
    api.delete<{ success: boolean; message: string }>(`/dealer/images/${id}`);

export const uploadCompanyLogo = (file: File) => {
    const formData = new FormData();
    formData.append("logo", file);
    return apiFile.put<{ success: boolean; data: any; message: string }>("/dealer/logo", formData);
};

export const deleteCompanyLogo = () =>
    api.delete<{ success: boolean; message: string }>(`/dealer/logo`);

// Videos
export const uploadDealerVideo = (file: File) => {
    const formData = new FormData();
    formData.append("video", file);
    return apiFile.post<{ success: boolean; data: any; message: string }>("/dealer/videos", formData);
};

export const getDealerVideos = () =>
    api.get<{ success: boolean; data: any[] }>("/dealer/videos");

export const updateDealerVideo = (id: number | string, data: { title?: string; description?: string }) =>
    api.put<{ success: boolean; data: any; message: string }>(`/dealer/videos/${id}`, data);

export const deleteDealerVideo = (id: number | string) =>
    api.delete<{ success: boolean; message: string }>(`/dealer/videos/${id}`);

// ========================
// SOCIAL LINKS API ENDPOINTS
// ========================
export const getDealerSocialLinks = () =>
    api.get<{ success: boolean; data: any }>("/dealer/social");

export const updateDealerSocialLinks = (data: { facebook?: string; instagram?: string; linkedin?: string; twitter?: string }) =>
    api.put<{ success: boolean; message: string; data: any }>("/dealer/social", data);

// ========================
// SCHEDULE API ENDPOINTS
// ========================
export interface DealerSchedule {
    mondayOpen?: string | null;
    mondayClose?: string | null;
    mondayAppointmentOnly?: boolean;
    tuesdayOpen?: string | null;
    tuesdayClose?: string | null;
    tuesdayAppointmentOnly?: boolean;
    wednesdayOpen?: string | null;
    wednesdayClose?: string | null;
    wednesdayAppointmentOnly?: boolean;
    thursdayOpen?: string | null;
    thursdayClose?: string | null;
    thursdayAppointmentOnly?: boolean;
    fridayOpen?: string | null;
    fridayClose?: string | null;
    fridayAppointmentOnly?: boolean;
    saturdayOpen?: string | null;
    saturdayClose?: string | null;
    saturdayAppointmentOnly?: boolean;
    sundayOpen?: string | null;
    sundayClose?: string | null;
    sundayAppointmentOnly?: boolean;
}

export const getDealerSchedule = () =>
    api.get<{ success: boolean; data: DealerSchedule }>("/dealer/schedule");

export const updateDealerSchedule = (data: DealerSchedule) =>
    api.put<{ success: boolean; message: string; data: DealerSchedule }>("/dealer/schedule", data);

export const getDealerRevenue = () =>
    api.get<{ success: boolean; message: string; data?: any }>("/dealer/revenue/me");

export const getShopifyAssociate = async () => {
    const cached = getSessionCache<ShopifyAssociateResponse>("shopify_associate_details");
    if (cached) return { data: cached } as any;
    const res = await api.get<ShopifyAssociateResponse>("/dealer/shopify/associate");
    if (res.data?.success) setSessionCache("shopify_associate_details", res.data, 10);
    return res;
};

// ========================
// GAMIFICATION ENDPOINTS
// ========================
export const submitTvSales = (data: { tvSalesCount: number; period: string }) =>
    api.post<{ success: boolean; message: string; data?: any }>("/dealer/tv-sales", data);

export const getTvSalesStatus = () =>
    api.get<{ success: boolean; message: string; data: any }>("/dealer/tv-sales/status");

export const getTvSalesHistory = () =>
    api.get<{ success: boolean; message: string; data: any }>("/dealer/tv-sales/history");

// ========================
// AI PROFILE GENERATION
// ========================
export interface GenerateBioResponse {
    success: boolean;
    message: string;
    data: {
        companyName?: string;
        storeType?: string;
        location?: string;
        aboutUs: string;
        tagline?: string;
        bulletHighlights?: string[];
    };
}

export const generateProfileBio = (saveToProfile: boolean = true) =>
    api.post<GenerateBioResponse>("/dealer/profile/generate-bio", { saveToProfile });

// ========================
// AI PROFILE OVERVIEW
// ========================
export interface AiOverviewData {
    dealerId?: number;
    companyName?: string;
    aiSummary: string;
    highlights?: string[];
    isAiSummaryAvailable?: boolean;
    savedToProfile?: boolean;
}

export interface AiOverviewResponse {
    success: boolean;
    message: string;
    data: AiOverviewData;
    timestamp?: string;
}

export const generateAiOverview = (saveToProfile: boolean = true) =>
    api.post<AiOverviewResponse>("/dealer/ai-overview", { saveToProfile });

// ========================
// ADMIN SURVEY & PORTAL FEEDBACK APIs
// ========================

export const getAdminSurveyResponses = (params?: SurveySubmissionsQueryParams) => {
    const query = new URLSearchParams();
    if (params?.page) query.append("page", params.page.toString());
    if (params?.limit) query.append("limit", params.limit.toString());
    if (params?.search) query.append("search", params.search);
    if (params?.surveyId) query.append("surveyId", params.surveyId.toString());
    if (params?.dateFrom) query.append("dateFrom", params.dateFrom);
    if (params?.dateTo) query.append("dateTo", params.dateTo);

    const queryString = query.toString();
    const endpoint = `/admin/surveys/responses${queryString ? `?${queryString}` : ""}`;
    return api.get<SurveySubmissionsResponse>(endpoint);
};

export const getAdminSurveys = () =>
    api.get<{ success: boolean; data: SurveyForm[] }>("/admin/surveys");

export const getAdminSurveyById = (surveyId: number | string) =>
    api.get<{ success: boolean; data: SurveyForm }>(`/admin/surveys/${surveyId}`);

export const toggleSurveyActive = (surveyId: number | string, isActive: boolean) =>
    api.patch<{ success: boolean; message: string; data?: SurveyForm }>(`/admin/surveys/${surveyId}/toggle-active`, { isActive });

export const exportAdminSurveyResponses = (surveyId?: number | string) => {
    const endpoint = surveyId
        ? `/admin/surveys/${surveyId}/export`
        : `/admin/surveys/responses/export`;
    return api.get(endpoint, {
        responseType: "blob",
    });
};

export const createAdminSurvey = (payload: CreateSurveyPayload) =>
    api.post<{ success: boolean; message: string; data: SurveyForm }>("/admin/surveys", payload);

export const updateAdminSurvey = (surveyId: number | string, payload: UpdateSurveyPayload) =>
    api.put<{ success: boolean; message: string; data: SurveyForm }>(`/admin/surveys/${surveyId}`, payload);

export const deleteAdminSurvey = (surveyId: number | string) =>
    api.delete<{ success: boolean; message: string }>(`/admin/surveys/${surveyId}`);

export const analyzeAdminFeedback = async (feedbackId: number | string) => {
    try {
        return await api.post<{ success: boolean; data: AiFeedbackAnalysis }>(`/admin/feedbacks/${feedbackId}/analyze`);
    } catch (err: any) {
        if (err.response?.status === 404) {
            return await api.post<{ success: boolean; data: AiFeedbackAnalysis }>(`/admin/surveys/responses/${feedbackId}/analyze`);
        }
        throw err;
    }
};

// ========================
// DEALER PORTAL SURVEY APIs (from Postman collection)
// ========================

export const getActiveDealerSurvey = async () => {
    try {
        return await api.get<{ success: boolean; data: SurveyForm }>("/dealer/surveys/active");
    } catch (err: any) {
        if (err.response?.status === 404) {
            // Try alternative global survey route
            return await api.get<{ success: boolean; data: SurveyForm }>("/surveys/active");
        }
        throw err;
    }
};

export const submitDealerSurveyResponse = async (payload: SubmitDealerSurveyPayload) => {
    try {
        return await api.post<{ success: boolean; message: string; data?: any }>("/dealer/surveys/submit", payload);
    } catch (err: any) {
        if (err.response?.status === 404) {
            // Try alternative global survey route
            return await api.post<{ success: boolean; message: string; data?: any }>("/surveys/submit", payload);
        }
        throw err;
    }
};

// ========================
// DEALER AI REPORT APIs
// ========================

export const getDealerAiReportPdf = (year: number | string = new Date().getFullYear()) => {
    return api.get(`/dealer/reports/pdf?year=${year}`, {
        responseType: "blob",
    });
};

export const clearDealerReportsCache = async () => {
    try {
        return await api.post<{ success: boolean; message?: string }>("/dealer/reports/clear-cache");
    } catch (err: any) {
        if (err.response?.status === 405) {
            try {
                return await api.delete<{ success: boolean; message?: string }>("/dealer/reports/clear-cache");
            } catch {
                return await api.get<{ success: boolean; message?: string }>("/dealer/reports/clear-cache");
            }
        }
        throw err;
    }
};





export const getDealerAiAudit = async (dealerId: number) => {
    return api.get(`/admin/dealers/${dealerId}/ai-audit`);
};

// ==========================================
// Dealer Activity Tracking APIs
// ==========================================

// Dealer Endpoints
export const pingDealerActivity = async (data: {
  sessionId: string;
  url: string;
  pageTitle: string;
  timeSpentSeconds: number;
  latitude?: number;
  longitude?: number;
  address?: string;
}) => {
  return api.post('/dealer/activity/ping', data);
};

export const recordDealerLogout = async (data: {
  sessionId: string;
  reason: 'MANUAL_LOGOUT' | 'TOKEN_EXPIRED' | 'INACTIVITY_TIMEOUT';
  latitude?: number;
  longitude?: number;
  address?: string;
}) => {
  return api.post('/dealer/activity/logout', data);
};

// Admin Endpoints
export const getAdminActivityLogs = async (params: { page: number; limit: number; dealerId?: number; logoutReason?: string; search?: string }) => {
  return api.get('/admin/activity-logs', { params });
};

export const getAdminActiveDealers = async () => {
  return api.get('/admin/activity-logs/active');
};

export const getAdminDealerActivitySummary = async (dealerId: number) => {
  return api.get(`/admin/activity-logs/dealers/${dealerId}`);
};

export const exportAdminActivityLogs = async () => {
  return api.get('/admin/activity-logs/export', { responseType: 'blob' });
};

// ========================
// 08. VERSION HISTORY & CHANGELOG ENDPOINTS
// ========================
export const getVersionHistory = async (forceRefresh: boolean = false) => {
  const cacheKey = "app_version_history";
  if (!forceRefresh) {
    const cached = getSessionCache<VersionHistoryResponse>(cacheKey);
    if (cached) return { data: cached } as any;
  }
  const res = await api.get<VersionHistoryResponse>("/dealer/version-history");
  if (res.data?.success) {
    setSessionCache(cacheKey, res.data, 10); // Cache for 10 minutes
  }
  return res;
};

export const createReleaseNote = async (data: CreateReleaseNotePayload) => {
  const res = await api.post<CreateReleaseNoteResponse>("/admin/version-history", data);
  // Clear cache upon new release
  setSessionCache("app_version_history", null, 0);
  return res;
};
